import { generatePath } from 'react-router';
import {
  faArrowsTurnToDots,
  faChartLineUp,
  faFileImport,
  faFiles,
  faFolderMagnifyingGlass,
  faGear,
  faKey,
  faList,
  faRadar,
  faShieldExclamation,
  faSquareCheck,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import {
  CAN_BULK_CLOSE_ALERTS,
  CAN_BULK_OVERRIDE_RISK,
  CAN_EXECUTE_SANCTION_LIST_UPDATE,
  CAN_EXECUTE_SCREENING_SEARCH,
  CAN_MODIFY_ALERT_THROUGH_FILTER,
  CAN_MODIFY_BRIDGE_SETTINGS,
  CAN_MODIFY_ORGANISATION_FEATURE,
  CAN_READ_ALERT,
  CAN_READ_ALERT_FILTER,
  CAN_READ_ALL_ORGANISATIONS,
  CAN_READ_BATCH,
  CAN_READ_CASE,
  CAN_READ_CUSTOM_LIST,
  CAN_READ_DASHBOARD,
  CAN_READ_OAUTH_CLIENT,
  CAN_READ_PERSON,
  CAN_READ_QUERY_TEMPLATE,
  CAN_READ_RATE_LIMIT_CONFIG,
  CAN_READ_SANCTION_ALERT,
  CAN_READ_SCENARIO,
  CAN_READ_SCREENING_ALERT_FILTER,
  CAN_READ_SCREENING_CLEARANCE_RULE,
  CAN_READ_TRANSACTION,
  CAN_READ_WEBHOOK,
  CAN_TEST_JMESPATH_EXPRESSION,
  CAN_TEST_SCREENING_CLEARANCE_RULE,
  CAN_USE_ENGINEERING_OPERATIONS,
  CAN_VIEW_DASHBOARD_MENU,
  CAN_VIEW_SYSTEM_MENU,
  CAN_VIEW_USER_MENU,
  configurationViewPermissions,
} from '../auth/permissions';
import { ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ConfigurationProduct } from '../settings/configuration/Configuration';
import { createSearchParams, To } from 'react-router-dom';
import { SearchParamKeys } from '../common/searchParamKeys';

const alertFilters = '/alert-filters';
const alertNoteTemplates = '/note-templates';
const alertStatuses = '/screening/statuses';
const apiConfiguration = '/credentials';
const automaticReScreening = '/screening/automatic-re-screening';
const bulkRiskOverride = '/bulk-risk-override';
const bulkStatusChange = '/bulk-status-change';
const cases = '/cases';
const caseCreation = '/cases/new';
const caseDetails = '/cases/:handle';
const clearanceRule = '/screening/clearance-rules/:handle';
const clearanceRules = '/screening/clearance-rules';
const clearanceRulesAdd = '/screening/clearance-rules/add/:entity';
const clearanceRulesAnalysis = '/screening/clearance-rules-analyses/:id';
const clearanceRulesSandbox = '/screening/clearance-rules/sandbox';
const configuration = '/configuration';
const customLists = '/custom-lists';
const customListAdd = '/custom-lists/add';
const customList = '/custom-lists/:customListId';
const customListRecord = '/custom-lists/:customListId/records/:recordId';
const dashboard = '/dashboard/:name';
const dashboards = '/dashboards';
const dataUpload = '/data-upload';
const decisionRules = '/decision-rules';
const features = '/features';
const jmespathSandbox = '/jmespath/sandbox';
const listGroups = '/screening/list-groups';
const login = '/login';
const manualAlert = '/manual/alerts/:id';
const mappings = '/mappings';
const monitoringAlert = '/monitoring/alerts/:id';
const monitoringPersonAlerts = '/person-alerts';
const monitoringTransactionAlerts = '/alerts';
const organisations = '/organisations';
const person = '/customer/:id';
const personSearch = '/search/customers';
const profile = '/profile';
const queryTemplates = '/querytemplates';
const rateLimiter = '/rate-limiter';
const risk = '/risk';
const riskRule = '/risk/:handle';
const riskOverrideReasons = '/risk/override-reasons';
const root = '/';
const scenarios = '/scenarios';
const scenario = '/scenarios/:handle';
const schemas = '/schemas';
const screeningAlertFilters = '/screening/alert-filters';
const screeningAlert = '/screening/alerts/:id';
const screeningFlows = '/screening/flows';
const screeningFlow = '/screening/flows/:flowId';
const screeningFlowDraftTestRuns = '/screening/flows/:flowId/drafts/:draftId/test-runs';
const screeningFlowWizard = '/screening/field/setup';
const screeningLists = '/screening/lists';
const screeningSearch = '/screening/search';
const screeningTests = '/screening/test';
const segments = '/segments';
const segment = '/segments/:code';
const significantUpdateIndicators = '/screening/significant-update-indicators';
const system = '/system';
const testRun = '/scenarios/:handle/content/:contentHandle/test-run/:testRunHandle';
const transaction = '/transactions/:id';
const transactionSearch = '/search/transactions';
const twoFactor = '/two-factor';
const users = '/users';
const webhooks = '/webhooks';
const transliterate = '/transliterate';
const bridgeAlert = '/bridge/alerts/:id';
const bridgeSettings = '/bridge-settings';
const engineeringOps = '/engineering-ops';

export const InternalNavigation = {
  alertFilters,
  alertNoteTemplates,
  alertStatuses,
  apiConfiguration,
  automaticReScreening,
  bulkRiskOverride,
  bulkStatusChange,
  cases,
  caseCreation,
  caseDetails,
  clearanceRule,
  clearanceRules,
  clearanceRulesAdd,
  clearanceRulesAnalysis,
  clearanceRulesSandbox,
  configuration,
  customList,
  customListAdd,
  customLists,
  dashboard,
  dashboards,
  dataUpload,
  decisionRules,
  features,
  jmespathSandbox,
  listGroups,
  login,
  manualAlert,
  mappings,
  monitoringAlert,
  monitoringPersonAlerts,
  monitoringTransactionAlerts,
  organisations,
  person,
  personSearch,
  profile,
  queryTemplates,
  rateLimiter,
  risk,
  riskRule,
  riskOverrideReasons,
  root,
  scenarios,
  schemas,
  screeningAlertFilters,
  screeningAlert,
  screeningFlows,
  screeningFlow,
  screeningFlowDraftTestRuns,
  screeningFlowWizard,
  screeningLists,
  screeningSearch,
  screeningTests,
  segments,
  segment,
  significantUpdateIndicators,
  system,
  transaction,
  transactionSearch,
  twoFactor,
  testRun,
  users,
  webhooks,
  transliterate,
  bridgeAlert,
  bridgeSettings,
  engineeringOps,
} as const;

export const getClearanceRulePath = (handle: string) => generatePath(clearanceRule, { handle });
export const getClearanceRulesAddPath = (entity: 'person' | 'transaction') =>
  generatePath(clearanceRulesAdd, { entity });
export const getClearanceRulesAnalysesPath = (id: string) => generatePath(clearanceRulesAnalysis, { id });
export const getDashboardPath = (name: string) => generatePath(dashboard, { name });
export const getScreeningFlowPath = (flowId: string) => generatePath(screeningFlow, { flowId });
export const getScreeningFlowDraftTestRunsPath = (flowId: string, draftId: number) =>
  generatePath(screeningFlowDraftTestRuns, { flowId, draftId: String(draftId) });
export const getManualAlertPath = (id: string | number) => generatePath(manualAlert, { id: String(id) });
export const getScreeningAlertPath = (id: number) => generatePath(screeningAlert, { id: String(id) });
export const getMonitoringAlertPath = (id: number) => generatePath(monitoringAlert, { id: String(id) });
export const getBridgeAlertPath = (id: number) => generatePath(bridgeAlert, { id: String(id) });
export const getSegmentPath = (code: string) => generatePath(segment, { code });
export const getPersonPath = (id: string) => generatePath(person, { id });
export const getTransactionPath = (id: string) => generatePath(transaction, { id });
export const getRiskRulePath = (handle: string) => generatePath(riskRule, { handle });
export const getScenarioPath = (handle: string) => generatePath(scenario, { handle });
export const getCasePath = (handle: string) => generatePath(caseDetails, { handle });
export const getCustomListPath = (customListId: string) => generatePath(customList, { customListId });
export const getCustomListRecordPath = (customListId: string, recordId: string) =>
  generatePath(customListRecord, { customListId, recordId });

export const getConfigurationUrl = (product: (typeof ConfigurationProduct)[keyof typeof ConfigurationProduct]): To => ({
  pathname: configuration,
  search: createSearchParams({ [SearchParamKeys.product]: product }).toString(),
});

export interface NavbarItemType {
  title: string;
  link?: string;
  icon: IconDefinition;
  subItems?: NavbarSubItemType[];
  isSectionEnd?: boolean;

  role?: string | string[];
  permission?: string | string[] | { permissions: string[]; rule: 'all' | 'any' };
  feature?: ORGANISATION_FEATURE | ORGANISATION_FEATURE[] | { features: ORGANISATION_FEATURE[]; rule: 'all' | 'any' };
  organisationNotRequired?: boolean;
}

export interface NavbarSubItemType extends Pick<
  NavbarItemType,
  'title' | 'role' | 'permission' | 'feature' | 'organisationNotRequired'
> {
  link: string;
}

export const navbarItems: NavbarItemType[] = [
  {
    title: 'Alerts',
    link: InternalNavigation.alertFilters,
    icon: faShieldExclamation,
    permission: [CAN_READ_ALERT_FILTER, CAN_MODIFY_ALERT_THROUGH_FILTER],
    feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING, ORGANISATION_FEATURE.BRIDGE],
  },
  {
    title: 'Screening alerts',
    link: InternalNavigation.screeningAlertFilters,
    icon: faShieldExclamation,
    permission: {
      permissions: [CAN_READ_SANCTION_ALERT, CAN_READ_SCREENING_ALERT_FILTER],
      rule: 'all',
    },
    feature: {
      features: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.SCREENING_ALERT_FILTERS],
      rule: 'all',
    },
  },
  {
    title: 'Transaction monitoring',
    link: InternalNavigation.monitoringTransactionAlerts,
    icon: faShieldExclamation,
    permission: CAN_READ_ALERT,
    feature: {
      features: [ORGANISATION_FEATURE.MONITORING, ORGANISATION_FEATURE.OLD_MONITORING_ALERTS_VIEWS],
      rule: 'all',
    },
  },
  {
    title: 'Person monitoring',
    link: InternalNavigation.monitoringPersonAlerts,
    icon: faShieldExclamation,
    permission: CAN_READ_ALERT,
    feature: {
      features: [ORGANISATION_FEATURE.MONITORING, ORGANISATION_FEATURE.OLD_MONITORING_ALERTS_VIEWS],
      rule: 'all',
    },
  },
  {
    title: 'Cases',
    link: InternalNavigation.cases,
    icon: faShieldExclamation,
    permission: [CAN_READ_CASE],
    feature: [ORGANISATION_FEATURE.CASE_MANAGEMENT],
  },
  {
    title: 'Screening Search',
    link: InternalNavigation.screeningSearch,
    icon: faRadar,
    permission: CAN_EXECUTE_SCREENING_SEARCH,
    feature: ORGANISATION_FEATURE.SANCTIONS,
  },
  {
    title: 'Entity Search',
    icon: faFolderMagnifyingGlass,
    isSectionEnd: true,
    subItems: [
      {
        title: 'Person',
        link: InternalNavigation.personSearch,
        permission: CAN_READ_PERSON,
      },
      {
        title: 'Transaction',
        link: InternalNavigation.transactionSearch,
        permission: CAN_READ_TRANSACTION,
      },
    ],
    feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING],
  },
  {
    title: 'Dashboards',
    icon: faChartLineUp,
    permission: CAN_READ_DASHBOARD,
    feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING],
  },
  {
    title: 'Scenarios',
    link: InternalNavigation.scenarios,
    icon: faArrowsTurnToDots,
    permission: CAN_READ_SCENARIO,
    feature: ORGANISATION_FEATURE.MONITORING,
  },
  {
    title: 'Custom Lists',
    link: InternalNavigation.customLists,
    icon: faList,
    permission: CAN_READ_CUSTOM_LIST,
    feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING, ORGANISATION_FEATURE.RISK],
  },
  {
    title: 'Query Templates',
    link: InternalNavigation.queryTemplates,
    icon: faFiles,
    permission: CAN_READ_QUERY_TEMPLATE,
    feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING, ORGANISATION_FEATURE.RISK],
  },
  {
    title: 'Clearance Rules',
    link: InternalNavigation.clearanceRules,
    icon: faSquareCheck,
    permission: CAN_READ_SCREENING_CLEARANCE_RULE,
    feature: ORGANISATION_FEATURE.SANCTIONS,
  },
  {
    title: 'Data Upload',
    link: InternalNavigation.dataUpload,
    icon: faFileImport,
    permission: CAN_READ_BATCH,
    feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING],
  },
  {
    title: 'Configuration',
    link: configuration,
    icon: faGear,
    permission: configurationViewPermissions,
  },
  {
    title: 'Admin',
    icon: faKey,
    organisationNotRequired: true,
    subItems: [
      {
        title: 'Bulk Status Change',
        link: InternalNavigation.bulkStatusChange,
        permission: CAN_BULK_CLOSE_ALERTS,
        feature: ORGANISATION_FEATURE.CUSTOMER_SEES_BULK_STATUS_CHANGE,
      },
      {
        title: 'Bulk Risk Override',
        link: InternalNavigation.bulkRiskOverride,
        permission: CAN_BULK_OVERRIDE_RISK,
        feature: ORGANISATION_FEATURE.CUSTOMER_SEES_BULK_RISK_OVERRIDE,
      },
      {
        title: 'Users',
        link: InternalNavigation.users,
        permission: [CAN_VIEW_USER_MENU],
        organisationNotRequired: true,
      },
      {
        title: 'Dashboard Management',
        link: InternalNavigation.dashboards,
        permission: CAN_VIEW_DASHBOARD_MENU,
        feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING],
      },
      {
        title: 'API Configuration',
        link: InternalNavigation.apiConfiguration,
        permission: CAN_READ_OAUTH_CLIENT,
        feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING],
      },
      {
        title: 'Bridge Settings',
        link: InternalNavigation.bridgeSettings,
        permission: CAN_MODIFY_BRIDGE_SETTINGS,
        feature: [ORGANISATION_FEATURE.BRIDGE],
      },
      {
        title: 'Webhooks',
        link: InternalNavigation.webhooks,
        permission: CAN_READ_WEBHOOK,
        feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING],
      },
      {
        title: 'System',
        link: InternalNavigation.system,
        permission: CAN_VIEW_SYSTEM_MENU,
        feature: [ORGANISATION_FEATURE.SANCTIONS, ORGANISATION_FEATURE.MONITORING, ORGANISATION_FEATURE.BRIDGE],
      },
    ],
  },
  {
    title: 'Global Admin',
    icon: faWandMagicSparkles,
    role: ['ADMIN', 'GLOBAL_ENGINEER'],
    organisationNotRequired: true,
    subItems: [
      {
        title: 'Organisations',
        link: InternalNavigation.organisations,
        permission: CAN_READ_ALL_ORGANISATIONS,
        organisationNotRequired: true,
      },
      {
        title: 'Features',
        link: InternalNavigation.features,
        permission: CAN_MODIFY_ORGANISATION_FEATURE,
      },
      {
        title: 'Screening Lists',
        link: InternalNavigation.screeningLists,
        permission: CAN_EXECUTE_SANCTION_LIST_UPDATE,
      },
      {
        title: 'Bulk Status Change',
        link: InternalNavigation.bulkStatusChange,
        permission: CAN_BULK_CLOSE_ALERTS,
      },
      {
        title: 'Bulk Risk Override',
        link: InternalNavigation.bulkRiskOverride,
        permission: CAN_BULK_OVERRIDE_RISK,
      },
      {
        title: 'Rate Limiter',
        link: InternalNavigation.rateLimiter,
        permission: CAN_READ_RATE_LIMIT_CONFIG,
      },
      {
        title: 'JMESPath Sandbox',
        link: InternalNavigation.jmespathSandbox,
        permission: CAN_TEST_JMESPATH_EXPRESSION,
      },
      {
        title: 'Clearance Rules Sandbox',
        link: InternalNavigation.clearanceRulesSandbox,
        permission: CAN_TEST_SCREENING_CLEARANCE_RULE,
      },
      {
        title: 'Screening Tests',
        link: InternalNavigation.screeningTests,
        feature: ORGANISATION_FEATURE.SANCTIONS,
      },
      {
        title: 'Engineering Ops',
        link: InternalNavigation.engineeringOps,
        permission: CAN_USE_ENGINEERING_OPERATIONS,
      },
    ],
  },
];
